import * as uuid from "uuid";

import api from "../api";
import { AgencyType } from "../types/agencyTypes";

const FEDERATED_USERNAME_PATTERN = /^(google|facebook|loginwithamazon|signinwithapple|microsoft)_/i;
const MISSING_AGENCY_ERROR = "No agency found";

const inFlightByUsername = new Map<string, Promise<AgencyType | undefined>>();

const getStringValue = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

const getIdTokenPayload = (user: any): Record<string, unknown> => {
  const payload = user?.signInUserSession?.idToken?.payload;
  if (payload && typeof payload === "object") {
    return payload;
  }
  return {};
};

const getUserClaim = (user: any, key: string): string => {
  const attributes = user?.attributes && typeof user.attributes === "object" ? user.attributes : {};
  const payload = getIdTokenPayload(user);
  return getStringValue(attributes[key]) || getStringValue(payload[key]);
};

const isPlaceholderName = (value: string, userName: string): boolean => {
  if (!value) {
    return true;
  }
  if (value === userName) {
    return true;
  }
  return FEDERATED_USERNAME_PATTERN.test(value);
};

const getAgencyDisplayName = (user: any, userName: string): string => {
  const name = getUserClaim(user, "name");
  if (name && !isPlaceholderName(name, userName)) {
    return name;
  }

  const givenName = getUserClaim(user, "given_name");
  const familyName = getUserClaim(user, "family_name");
  const fullName = [givenName, familyName].filter(Boolean).join(" ");
  if (fullName) {
    return fullName;
  }

  const email = getUserClaim(user, "email");
  const emailLocalPart = email.split("@")[0];
  if (emailLocalPart) {
    return emailLocalPart;
  }

  return userName;
};

const loadOrCreateAgencyForUser = async (user: any): Promise<AgencyType | undefined> => {
  const userName = user?.username;
  if (!userName) {
    return undefined;
  }

  const email = getUserClaim(user, "email");
  const displayName = getAgencyDisplayName(user, userName);

  const existing = await api.getAgencyByUserName(userName, true);
  if (existing.success && existing.payload) {
    if (isPlaceholderName(existing.payload.name, userName) && displayName !== existing.payload.name) {
      return {
        ...existing.payload,
        name: displayName,
        email: existing.payload.email || email,
      };
    }
    return existing.payload;
  }

  if (existing.error && existing.error !== MISSING_AGENCY_ERROR) {
    return undefined;
  }

  const created = await api.createBrandgenAgency({
    agencyId: `bgAgency-${uuid.v4()}`,
    createdBy: userName,
    email,
    name: displayName,
    users: [userName],
  });

  if (created.success && created.payload) {
    return created.payload;
  }

  return undefined;
};

export const ensureAgencyForUser = async (user: any): Promise<AgencyType | undefined> => {
  const userName = user?.username;
  if (!userName) {
    return undefined;
  }

  const inFlight = inFlightByUsername.get(userName);
  if (inFlight) {
    return inFlight;
  }

  const request = loadOrCreateAgencyForUser(user).finally(() => {
    inFlightByUsername.delete(userName);
  });
  inFlightByUsername.set(userName, request);
  return request;
};
